import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    if (!supabase || !userId) {
      setProfile(null);
      return null;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, full_name, is_admin, created_at")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      setProfile(null);
      return null;
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return;
    }

    let mounted = true;

    (async () => {
      const {
        data: { session: s },
      } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(s);
      if (s?.user?.id) await loadProfile(s.user.id);
      else setProfile(null);
      if (mounted) setLoading(false);
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      if (s?.user?.id) {
        setLoading(true);
        await loadProfile(s.user.id);
        setLoading(false);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async (email, password) => {
    if (!supabase)
      return { error: new Error("Supabase is not configured (check .env).") };
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  /** Call right after signInWithPassword so session + profile exist before navigating to /admin */
  const applySessionFromAuth = useCallback(async (newSession) => {
    setSession(newSession ?? null);
    if (newSession?.user?.id) {
      setLoading(true);
      await loadProfile(newSession.user.id);
      setLoading(false);
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [loadProfile]);

  const signUp = useCallback(async (email, password, fullName, meta = {}) => {
    if (!supabase)
      return { error: new Error("Supabase is not configured (check .env).") };
    return supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, ...meta } },
    });
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return { error: null };
    return supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({
      session,
      profile,
      loading,
      isAdmin: Boolean(profile?.is_admin),
      signIn,
      signUp,
      signOut,
      applySessionFromAuth,
      refreshProfile: async (userId) => {
        const uid = userId ?? session?.user?.id;
        if (!uid) return null;
        return loadProfile(uid);
      },
    }),
    [
      session,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      applySessionFromAuth,
      loadProfile,
    ]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
